import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";
import { StatCard } from "../../components/admin/StatCard";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { apiError } from "../../lib/apiError";

type DBPoolStats = {
  pool_size: number;
  checked_in: number;
  checked_out: number;
  overflow: number;
  total_connections: number;
  utilization_pct: number;
  configured_pool_size: number;
  configured_max_overflow: number;
  status: "healthy" | "warning" | "critical" | "unavailable";
  error?: string;
};

type RequestMetrics = {
  enabled: boolean;
  message?: string;
  window_hours?: number;
  total_requests?: number;
  avg_duration_ms?: number;
  p50_duration_ms?: number;
  p95_duration_ms?: number;
  p99_duration_ms?: number;
  error_count?: number;
  error_rate?: number;
  by_endpoint?: Array<{
    path: string;
    count: number;
    avg_ms: number;
    errors: number;
  }>;
  by_status?: Record<string, number>;
};

type SystemStats = {
  total_users: number;
  active_sessions: number;
  total_animals: number;
  error?: string;
};

type SlowQuery = {
  timestamp: string;
  duration_ms: number;
  statement: string;
};

type AutoPullMetrics = {
  enabled: boolean;
  message?: string;
  window_hours?: number;
  total_pulls?: number;
  total_skipped?: number;
  total_success?: number;
  total_failed?: number;
  success_rate?: number;
  avg_duration_ms?: number;
  p50_duration_ms?: number;
  p95_duration_ms?: number;
  by_trigger?: Record<string, number>;
};

type ErrorSummary = {
  enabled: boolean;
  message?: string;
  window_hours?: number;
  total_errors?: number;
  by_type?: Record<string, number>;
  recent?: Array<{
    timestamp: string;
    type: string;
    path: string;
    error: string;
  }>;
};

type PerformanceOverview = {
  db_pool: DBPoolStats;
  request_metrics_1h: RequestMetrics;
  request_metrics_24h: RequestMetrics;
  system_stats: SystemStats;
};

export function AdminPerformancePage() {
  const [overview, setOverview] = useState<PerformanceOverview | null>(null);
  const [slowQueries, setSlowQueries] = useState<SlowQuery[]>([]);
  const [autoPull, setAutoPull] = useState<AutoPullMetrics | null>(null);
  const [errors, setErrors] = useState<ErrorSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [overviewResult, slowQueriesResult, autoPullResult, errorsResult] =
        await Promise.all([
          api.GET("/api/platform/performance/overview"),
          api.GET("/api/platform/performance/slow-queries", {
            params: { query: { limit: 20 } },
          }),
          api.GET("/api/platform/performance/auto-pull", {
            params: { query: { hours: 24 } },
          }),
          api.GET("/api/platform/performance/errors", {
            params: { query: { hours: 24 } },
          }),
        ]);

      if (cancelled) {
        return;
      }

      if (overviewResult.error) {
        setError(apiError(overviewResult.error, "Nie udało się wczytać metryk wydajności."));
        return;
      }

      setOverview(overviewResult.data as PerformanceOverview);

      if (slowQueriesResult.data) {
        setSlowQueries((slowQueriesResult.data as { slow_queries: SlowQuery[] }).slow_queries);
      }

      if (autoPullResult.data) {
        setAutoPull(autoPullResult.data as AutoPullMetrics);
      }

      if (errorsResult.data) {
        setErrors(errorsResult.data as ErrorSummary);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  function refresh() {
    setRefreshKey((k) => k + 1);
  }

  const poolStatus = overview?.db_pool.status || "unavailable";
  const poolColor =
    poolStatus === "healthy" ? "green" : poolStatus === "warning" ? "orange" : "red";

  return (
    <div className="page__content page__content--admin">
      <AdminPageIntro
        actions={
          <button type="button" className="btn btn--ghost" onClick={refresh}>
            Odśwież
          </button>
        }
      >
        Monitorowanie wydajności aplikacji w czasie rzeczywistym.
      </AdminPageIntro>

      {error ? (
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      ) : null}

      {/* DB Pool Stats */}
      <section className="admin-section" aria-label="Database Connection Pool">
        <h2 className="admin-section__title">
          Database Connection Pool{" "}
          <span style={{ color: poolColor, fontSize: "0.875em" }}>
            ({overview?.db_pool.status || "..."})
          </span>
        </h2>
        {overview?.db_pool.error ? (
          <AlertBanner variant="warn" message={`Pool error: ${overview.db_pool.error}`} />
        ) : (
          <>
            <div className="admin-kpi">
              <StatCard
                label="Utilization"
                value={
                  overview ? `${overview.db_pool.utilization_pct}%` : "—"
                }
              />
              <StatCard
                label="Checked Out"
                value={
                  overview
                    ? `${overview.db_pool.checked_out} / ${overview.db_pool.total_connections}`
                    : "—"
                }
              />
              <StatCard
                label="Overflow"
                value={
                  overview
                    ? `${overview.db_pool.overflow} / ${overview.db_pool.configured_max_overflow}`
                    : "—"
                }
              />
              <StatCard
                label="Pool Size"
                value={overview ? `${overview.db_pool.configured_pool_size}` : "—"}
              />
            </div>
            {overview && overview.db_pool.utilization_pct > 80 ? (
              <AlertBanner
                variant="warn"
                message={`Pool utilization >80% - rozważ zwiększenie DATABASE_POOL_SIZE (obecnie ${overview.db_pool.configured_pool_size}).`}
              />
            ) : null}
          </>
        )}
      </section>

      {/* Request Metrics */}
      <section className="admin-section" aria-label="HTTP Request Metrics">
        <h2 className="admin-section__title">HTTP Requests</h2>
        {overview?.request_metrics_1h.enabled === false ? (
          <p className="muted">{overview.request_metrics_1h.message}</p>
        ) : (
          <>
            <div className="admin-subsection">
              <h3 className="admin-subsection__title">Ostatnia godzina</h3>
              <div className="admin-kpi">
                <StatCard
                  label="Total Requests"
                  value={overview?.request_metrics_1h.total_requests?.toString() || "—"}
                />
                <StatCard
                  label="Avg Duration"
                  value={
                    overview?.request_metrics_1h.avg_duration_ms
                      ? `${overview.request_metrics_1h.avg_duration_ms}ms`
                      : "—"
                  }
                />
                <StatCard
                  label="P95 Latency"
                  value={
                    overview?.request_metrics_1h.p95_duration_ms
                      ? `${overview.request_metrics_1h.p95_duration_ms}ms`
                      : "—"
                  }
                />
                <StatCard
                  label="P99 Latency"
                  value={
                    overview?.request_metrics_1h.p99_duration_ms
                      ? `${overview.request_metrics_1h.p99_duration_ms}ms`
                      : "—"
                  }
                />
                <StatCard
                  label="Error Rate"
                  value={
                    overview?.request_metrics_1h.error_rate !== undefined
                      ? `${(overview.request_metrics_1h.error_rate * 100).toFixed(1)}%`
                      : "—"
                  }
                />
              </div>
              {overview &&
              overview.request_metrics_1h.p95_duration_ms &&
              overview.request_metrics_1h.p95_duration_ms > 500 ? (
                <AlertBanner
                  variant="warn"
                  message={`P95 latency >500ms - sprawdź slow queries i DB pool.`}
                />
              ) : null}
            </div>

            {overview?.request_metrics_1h.by_endpoint &&
            overview.request_metrics_1h.by_endpoint.length > 0 ? (
              <div className="admin-subsection">
                <h3 className="admin-subsection__title">Top Endpoints (1h)</h3>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Path</th>
                        <th style={{ textAlign: "right" }}>Count</th>
                        <th style={{ textAlign: "right" }}>Avg ms</th>
                        <th style={{ textAlign: "right" }}>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.request_metrics_1h.by_endpoint.map((endpoint, idx) => (
                        <tr key={idx}>
                          <td>
                            <code style={{ fontSize: "0.875rem" }}>{endpoint.path}</code>
                          </td>
                          <td style={{ textAlign: "right" }}>{endpoint.count}</td>
                          <td style={{ textAlign: "right" }}>{endpoint.avg_ms.toFixed(1)}</td>
                          <td style={{ textAlign: "right", color: endpoint.errors > 0 ? "red" : undefined }}>
                            {endpoint.errors}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>

      {/* Slow Queries */}
      {slowQueries.length > 0 ? (
        <section className="admin-section" aria-label="Slow Queries">
          <h2 className="admin-section__title">Slow Queries (&gt;100ms)</h2>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th style={{ textAlign: "right" }}>Duration</th>
                  <th>Statement</th>
                </tr>
              </thead>
              <tbody>
                {slowQueries.slice(0, 10).map((query, idx) => (
                  <tr key={idx}>
                    <td style={{ fontSize: "0.875rem" }}>
                      {new Date(query.timestamp).toLocaleString("pl-PL")}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        color: query.duration_ms > 500 ? "red" : "orange",
                      }}
                    >
                      {query.duration_ms.toFixed(1)}ms
                    </td>
                    <td>
                      <code style={{ fontSize: "0.8125rem" }}>{query.statement}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* Auto-pull Metrics */}
      {autoPull && autoPull.enabled && autoPull.total_pulls ? (
        <section className="admin-section" aria-label="PWA Auto-pull Metrics">
          <h2 className="admin-section__title">PWA Auto-pull (ostatnie 24h)</h2>
          <div className="admin-kpi">
            <StatCard label="Total Pulls" value={autoPull.total_pulls?.toString() || "—"} />
            <StatCard label="Skipped (TTL)" value={autoPull.total_skipped?.toString() || "—"} />
            <StatCard
              label="Success Rate"
              value={
                autoPull.success_rate !== undefined
                  ? `${(autoPull.success_rate * 100).toFixed(1)}%`
                  : "—"
              }
            />
            <StatCard
              label="P95 Duration"
              value={autoPull.p95_duration_ms ? `${autoPull.p95_duration_ms}ms` : "—"}
            />
          </div>
          {autoPull.success_rate && autoPull.success_rate < 0.9 ? (
            <AlertBanner
              variant="warn"
              message={`Auto-pull success rate <90% - sprawdź failed requests i rate limits.`}
            />
          ) : null}
          {autoPull.by_trigger && Object.keys(autoPull.by_trigger).length > 0 ? (
            <div className="admin-subsection">
              <h3 className="admin-subsection__title">By Trigger</h3>
              <div className="admin-kpi">
                {Object.entries(autoPull.by_trigger).map(([trigger, count]) => (
                  <StatCard key={trigger} label={trigger} value={count.toString()} />
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Error Summary */}
      {errors && errors.enabled && errors.total_errors !== undefined && errors.total_errors > 0 ? (
        <section className="admin-section" aria-label="Error Summary">
          <h2 className="admin-section__title">Errors (ostatnie 24h)</h2>
          <div className="admin-kpi">
            <StatCard label="Total Errors" value={errors.total_errors.toString()} />
            {errors.by_type && Object.keys(errors.by_type).length > 0 ? (
              <>
                {Object.entries(errors.by_type)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 4)
                  .map(([type, count]) => (
                    <StatCard key={type} label={type} value={count.toString()} />
                  ))}
              </>
            ) : null}
          </div>
          {errors.recent && errors.recent.length > 0 ? (
            <div className="admin-subsection">
              <h3 className="admin-subsection__title">Recent Errors</h3>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Type</th>
                      <th>Path</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.recent.slice(0, 10).map((err, idx) => (
                      <tr key={idx}>
                        <td style={{ fontSize: "0.875rem" }}>
                          {new Date(err.timestamp).toLocaleString("pl-PL")}
                        </td>
                        <td>
                          <code style={{ fontSize: "0.8125rem" }}>{err.type}</code>
                        </td>
                        <td>
                          <code style={{ fontSize: "0.8125rem" }}>{err.path}</code>
                        </td>
                        <td style={{ fontSize: "0.875rem" }}>{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* System Stats */}
      {overview?.system_stats ? (
        <section className="admin-section" aria-label="System Stats">
          <h2 className="admin-section__title">System Stats</h2>
          <div className="admin-kpi">
            <StatCard label="Total Users" value={overview.system_stats.total_users.toString()} />
            <StatCard
              label="Active Sessions"
              value={overview.system_stats.active_sessions.toString()}
            />
            <StatCard
              label="Total Animals"
              value={overview.system_stats.total_animals.toString()}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
